import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/* CSS */
import styles from './MyTripsPage.module.css';
import shared from '../styles/shared.module.css';

/* List/Card components */
import TripList from '../components/trips/TripList';
import TripCard from '../components/trips/TripCard';

/* Icons */
import searchIcon from "../assets/icons/search_icon.svg";
import toggleListIcon from "../assets/icons/toggle_list.svg";
import toggleCardIcon from "../assets/icons/toggle_card.svg";

/* Utils */
import { filterTrips } from '../utils/filterUtils';
import { splitTripsByDate } from '../utils/dateUtils';
import { tripUtils } from '../utils/tripUtils';

/* Context */
import { useToast } from '../context/ToastContext';

/* How many rows to show when collapsed */
const MAX_LIST_ROWS_COLLAPSED = 3;
const MAX_CARD_ROWS_COLLAPSED = 6;

const MyTripsPage = () => {

    const [allTrips, setAllTrips] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const { upcoming, past } = splitTripsByDate(allTrips);

    const [viewMode, setViewMode] = useState(() => {
        if (typeof window === 'undefined') return 'card';
        return localStorage.getItem('myTripsViewMode') || 'card';
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [showAllUpcoming, setShowAllUpcoming] = useState(false);
    const [showAllPast, setShowAllPast] = useState(false);

    const filteredUpcoming = filterTrips(upcoming, searchQuery);
    const filteredPast = filterTrips(past, searchQuery);

    const { showToast } = useToast();

    /* Navigation */
    const navigate = useNavigate();
    const handleCreateNewTrip = () => {
        navigate('/new', { state: { from: '/trips' } });
    };

    useEffect(() => {
        const fetchTrips = async () => {
            try {
                const storedTrips = await tripUtils.getAllTrips();
                setAllTrips(storedTrips);
            } catch (error) {
                console.error("Error fetching trips:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTrips();
    }, []);

    /* Persist view mode in localStorage */
    useEffect(() => {
        localStorage.setItem('myTripsViewMode', viewMode);
    }, [viewMode]);

    /* Reset show all when search query or view mode changes */
    useEffect(() => {
        setShowAllUpcoming(false);
        setShowAllPast(false);
    }, [searchQuery, viewMode]);

    const upcomingVisible = showAllUpcoming
        ? filteredUpcoming
        : filteredUpcoming.slice(0, viewMode === 'card' ? MAX_CARD_ROWS_COLLAPSED : MAX_LIST_ROWS_COLLAPSED);

    const pastVisible = showAllPast
        ? filteredPast
        : filteredPast.slice(0, viewMode === 'card' ? MAX_CARD_ROWS_COLLAPSED : MAX_LIST_ROWS_COLLAPSED);

    if (isLoading) {
        return <div className={shared.mainContent}>Loading your adventures...</div>;
    }

    const handleDeleteTrip = async (tripId) => {
        try {
            await tripUtils.deleteTrip(tripId);
            setAllTrips(prev => prev.filter(t => t.id !== tripId));
            showToast('Trip deleted', 'success');
        } catch (error) {
            console.error("Failed to delete trip", error);
            showToast('Could not delete trip. Please try again.', 'error');
        }
    };

    const handleDuplicateTrip = async (tripId) => {
        try {
            const newTrip = await tripUtils.duplicateTrip(tripId);
            setAllTrips(prev => [newTrip, ...prev]);
            showToast('Trip duplicated', 'success');
        } catch (error) {
            console.error("Failed to duplicate trip", error);
            showToast('Could not duplicate trip. Please try again.', 'error');
        }
    };

    return (
        <div>
            {/* Page header */}
            <section className={shared.heroHeader}>
                <div>
                    {/* Page title */}
                    <h1 className={shared.heroTitle}>My Trips</h1>
                    <p className={shared.heroSubtitle}>Ready to plan your next adventure?</p>

                    {/* View toggle */}
                    <div
                        className={styles.viewToggle}
                        aria-label="Toggle view mode"
                    >
                        {/* List view */}
                        <button
                            type="button"
                            className={`${styles.viewTogglePart} ${viewMode === 'list' ? styles.active : ''}`}
                            onClick={() => setViewMode('list')}
                            title="List view"
                        >
                            <img
                                src={toggleListIcon}
                                alt="List view"
                            />
                        </button>

                        {/* Card view */}
                        <button
                            type="button"
                            className={`${styles.viewTogglePart} ${viewMode === 'card' ? styles.active : ''}`}
                            onClick={() => setViewMode('card')}
                            title="Card view"
                        >
                            <img
                                src={toggleCardIcon}
                                alt="Card view"
                            />
                        </button>
                    </div>

                    {/* Search bar */}
                    <div className={shared.searchBar}>
                        <span>
                            <img
                                src={searchIcon}
                                alt="Search Icon"
                                className={shared.searchIcon}
                            />
                        </span>
                        <input
                            type="text"
                            placeholder="Quick trip lookup..."
                            aria-label="Search trips"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {/* Create new trip button */}
                    <button
                        className={styles.createTripButtonFull}
                        onClick={handleCreateNewTrip}
                    >
                        Create new trip
                    </button>
                </div>
            </section>

            {/* Upcoming trips */}
            <section className={styles.section}>
                <div className={shared.sectionHeader}>
                    <h2 className={shared.sectionTitle}>Upcoming Trips ({filteredUpcoming.length})</h2>
                    <span className={shared.sectionSubtitle}></span>
                </div>

                {/* Content or empty state */}
                {filteredUpcoming.length === 0 ? (
                    <p className={shared.emptyState}>
                        {upcoming.length === 0
                            ? "When you plan a new trip, it will show up here."
                            : "No trips match your search."}
                    </p>
                ) : viewMode === 'card' ? (
                    <div className={shared.cardRow}>
                        {upcomingVisible.map((trip) => (
                            <TripCard
                                key={trip.id}
                                trip={trip}
                                onDelete={handleDeleteTrip}
                                onDuplicate={handleDuplicateTrip}
                            />
                        ))}
                    </div>
                ) : (
                    <TripList
                        trips={upcomingVisible}
                        onDelete={handleDeleteTrip}
                        onDuplicate={handleDuplicateTrip}
                    />
                )
                }

                {/* View more/less button for upcoming trips */}
                {filteredUpcoming.length >
                    (viewMode === 'list'
                        ? MAX_LIST_ROWS_COLLAPSED
                        : MAX_CARD_ROWS_COLLAPSED) && (
                        <button
                            type="button"
                            className={styles.viewMoreButton}
                            onClick={() => setShowAllUpcoming(prev => !prev)}
                        >
                            {showAllUpcoming ? 'View less' : 'View more'}
                        </button>
                    )}
            </section>

            {/* Past trips */}
            <section className={styles.section}>
                <div className={shared.sectionHeader}>
                    <h2 className={shared.sectionTitle}>Past Trips ({filteredPast.length})</h2>
                    <span className={shared.sectionSubtitle}></span>
                </div>

                {/* Content or empty state */}
                {filteredPast.length === 0 ? (
                    <p className={shared.emptyState}>
                        {past.length === 0
                            ? "When your past trips are over, they will show up here."
                            : "No trips match your search."}
                    </p>
                ) : viewMode === 'card' ? (
                    <div className={shared.cardRow}>
                        {pastVisible.map((trip, index) => (
                            <TripCard
                                key={`${trip.id}-${trip.fromDate}-${index}`}
                                trip={trip}
                                onDelete={handleDeleteTrip}
                                onDuplicate={handleDuplicateTrip}
                            />
                        ))}
                    </div>
                ) : (
                    <TripList
                        trips={pastVisible}
                        onDelete={handleDeleteTrip}
                        onDuplicate={handleDuplicateTrip}
                    />
                )}

                {/* View more/less button for past trips */}
                {filteredPast.length >
                    (viewMode === 'list'
                        ? MAX_LIST_ROWS_COLLAPSED
                        : MAX_CARD_ROWS_COLLAPSED) && (
                        <button
                            type="button"
                            className={styles.viewMoreButton}
                            onClick={() => setShowAllPast(prev => !prev)}
                        >
                            {showAllPast ? 'View less' : 'View more'}
                        </button>
                    )}
            </section>
        </div>
    );
};

export default MyTripsPage;
